import React, { useState, useRef } from 'react';

interface TooltipProps {
  text: string;
  children: React.ReactElement;
  position?: 'top' | 'bottom';
}

/**
 * Wraps any element and shows a styled tooltip on hover.
 * Uses position:fixed + getBoundingClientRect so the tooltip is never clipped
 * by an ancestor with overflow:hidden or overflow:auto (e.g. scrollable lists).
 * Shows after a 120ms delay to avoid flickering on fast mouse-overs.
 */
const Tooltip: React.FC<TooltipProps> = ({ text, children, position = 'top' }) => {
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    timer.current = setTimeout(() => {
      if (wrapRef.current) {
        const r = wrapRef.current.getBoundingClientRect();
        setCoords({
          x: r.left + r.width / 2,
          y: position === 'top' ? r.top : r.bottom,
        });
      }
    }, 120);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setCoords(null);
  };

  return (
    <span ref={wrapRef} className="inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {coords && (
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            left: coords.x,
            ...(position === 'top'
              ? { bottom: window.innerHeight - coords.y + 8 }
              : { top: coords.y + 8 }),
            transform: 'translateX(-50%)',
          }}
          className="z-[9999] whitespace-nowrap rounded-md bg-gray-900 border border-white/10 px-2 py-1 text-xs text-gray-200 shadow-xl pointer-events-none"
        >
          {text}
          <span
            className={`absolute left-1/2 -translate-x-1/2 border-4 border-transparent ${
              position === 'top'
                ? 'top-full border-t-gray-900'
                : 'bottom-full border-b-gray-900'
            }`}
          />
        </span>
      )}
    </span>
  );
};

export default Tooltip;
